import React, { useState, useRef, useEffect } from 'react';
import { Modal, Button, Spinner } from 'react-bootstrap';
import { App } from '@capacitor/app';
import ComprobanteTicket from './ComprobanteTicket';

const N8N_PAYPHONE_LINK    = process.env.REACT_APP_N8N_PAYPHONE_LINK!;
const N8N_PAYPHONE_ESTADO  = process.env.REACT_APP_N8N_PAYPHONE_ESTADO!;
const N8N_COMPROBANTE      = process.env.REACT_APP_N8N_URL_COMPROBANTE!;
const N8N_DETALLE_FACTURA  = process.env.REACT_APP_N8N_URL_DETALLE_FACTURA!;

const TIMEOUT_SEG  = 180; // 3 minutos
const POLL_SEG     = 10;  // cada 10 segundos

interface PayphoneClienteModalProps {
  show: boolean;
  onHide: () => void;
  monto: number;
  cedula: string;
  nombre: string;
  estado?: string;
  facturaId?: string;
  clienteId?: string;
  onBrowserClose?: () => void;
}

type Etapa = 'inicio' | 'esperando' | 'pagado' | 'no_pagado' | 'timeout' | 'comprobante';

const PayphoneClienteModal: React.FC<PayphoneClienteModalProps> = ({
  show, onHide, monto, cedula, nombre, estado, facturaId,
}) => {
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState('');
  const [etapa,     setEtapa]     = useState<Etapa>('inicio');
  const [segundos,  setSegundos]  = useState(TIMEOUT_SEG);
  const [svgBase64, setSvgBase64] = useState('');

  const pollingRef  = useRef<ReturnType<typeof setInterval> | null>(null);
  const countRef    = useRef<ReturnType<typeof setInterval> | null>(null);
  const timeoutRef  = useRef<ReturnType<typeof setTimeout> | null>(null);
  const resolvedRef = useRef(false);

  const estadoUp    = estado?.toUpperCase() || '';
  const estadoColor = estadoUp === 'ACTIVO' ? 'var(--sn-exito-texto)' : 'var(--sn-peligro-texto)';
  const estadoBg    = estadoUp === 'ACTIVO' ? 'var(--sn-exito-suave)' : 'var(--sn-peligro-suave)';
  const estadoEmoji = estadoUp === 'ACTIVO' ? '🟢' : '🔴';

  const detener = () => {
    if (pollingRef.current)  { clearInterval(pollingRef.current);  pollingRef.current  = null; }
    if (countRef.current)    { clearInterval(countRef.current);    countRef.current    = null; }
    if (timeoutRef.current)  { clearTimeout(timeoutRef.current);   timeoutRef.current  = null; }
  };

  useEffect(() => {
    if (etapa !== 'inicio') return;
    setSegundos(TIMEOUT_SEG);
    resolvedRef.current = false;
  }, [etapa]);

  const fmtTime = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${String(sec).padStart(2, '0')}`;
  };

  const generarComprobante = async () => {
    try {
      const clientData = (() => { try { return JSON.parse(localStorage.getItem('clientData') || '{}'); } catch { return {}; } })();
      const now = new Date();
      const p2  = (n: number) => String(n).padStart(2, '0');
      const fechaImpresion = `${p2(now.getDate())}/${p2(now.getMonth()+1)}/${now.getFullYear()} ${p2(now.getHours())}:${p2(now.getMinutes())}:${p2(now.getSeconds())}`;
      const fechaHoy = `${p2(now.getDate())}/${p2(now.getMonth()+1)}/${now.getFullYear()}`;
      const fmtDate  = (d: string) => { if (!d || d.startsWith('0000')) return ''; const pts = d.split('-'); return pts.length === 3 ? `${pts[2]}/${pts[1]}/${pts[0]}` : d; };

      let planNombre='', facturacion='', suspension='', vencimiento='';
      let subtotal = monto.toFixed(2), iva='0.00', total=monto.toFixed(2), fechaPago=fechaHoy;

      try {
        const resD = await fetch(N8N_DETALLE_FACTURA, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ facturas_id: facturaId }),
        });
        const rawD   = await resD.json();
        const detalle = Array.isArray(rawD) ? rawD[0] : rawD;
        if (detalle?.estado === 'exito') {
          const fac   = detalle.factura || {};
          const items: any[] = detalle.items || [];
          const item  = items.find((i: any) => parseFloat(i.total) > 0) || items[0];
          const lines: string[] = (item?.descrp || '').split('\n').map((l: string) => l.trim()).filter(Boolean);
          planNombre  = lines[0] || '';
          facturacion = (lines[2] || '').replace('facturación del ', '');
          suspension  = (lines[3] || '').replace('Fecha de Suspensión : ', '');
          vencimiento = fmtDate(fac.vencimiento || '');
          fechaPago   = fmtDate(fac.fechapago || '') || fechaHoy;
          subtotal    = parseFloat(fac.subtotal  || monto.toString()).toFixed(2);
          iva         = parseFloat(fac.impuesto  || '0').toFixed(2);
          total       = parseFloat(fac.total     || monto.toString()).toFixed(2);
        }
      } catch {}

      const payload = {
        clienteNombre: nombre, clienteCedula: cedula,
        clienteCelular: clientData?.movil || '', clienteCorreo: clientData?.correo || '',
        facturaId: String(facturaId || '').padStart(8, '0'),
        planNombre, facturacion, suspension, fechaPago, vencimiento,
        subtotal, iva, total, saldo: '0.00', pasarela: 'Payphone',
        numeroComprobante: '', autorizacion: '',
        montoDepositado: monto.toFixed(2), fechaImpresion,
      };

      const res  = await fetch(N8N_COMPROBANTE, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (data?.base64) setSvgBase64(data.base64);
    } catch {}
  };

  const procesarPagado = async () => {
    if (resolvedRef.current) return;
    resolvedRef.current = true;
    detener();
    setEtapa('pagado');
    await generarComprobante();
  };

  const consultarEstado = async (): Promise<boolean> => {
    try {
      const res  = await fetch(N8N_PAYPHONE_ESTADO, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ facturaId }),
      });
      const data = await res.json();
      const raw  = Array.isArray(data) ? data[0] : data;
      if (raw?.estado?.toLowerCase() === 'pagado') {
        await procesarPagado();
        return true;
      }
    } catch {}
    return false;
  };

  const iniciarPollingYContador = () => {
    resolvedRef.current = false;
    setSegundos(TIMEOUT_SEG);

    // Contador regresivo visual
    countRef.current = setInterval(() => {
      setSegundos(s => {
        if (s <= 1) { clearInterval(countRef.current!); return 0; }
        return s - 1;
      });
    }, 1000);

    // Polling cada 5 segundos
    pollingRef.current = setInterval(async () => {
      if (resolvedRef.current) { detener(); return; }
      await consultarEstado();
    }, POLL_SEG * 1000);

    // Timeout 3 minutos
    timeoutRef.current = setTimeout(() => {
      if (!resolvedRef.current) {
        resolvedRef.current = true;
        detener();
        setEtapa('timeout');
      }
    }, TIMEOUT_SEG * 1000);

    // Cuando el usuario vuelve a la app → verificación inmediata
    App.addListener('appStateChange', async ({ isActive }) => {
      if (isActive && !resolvedRef.current) {
        await consultarEstado();
      }
    });
  };

  const handlePagar = async () => {
    setLoading(true);
    setError('');
    try {
      const montoInt = Math.round(monto * 100);
      const res  = await fetch(N8N_PAYPHONE_LINK, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cedula, nombre, monto: montoInt, facturaId: facturaId || '' }),
      });
      const data = await res.json();
      const raw  = Array.isArray(data) ? data[0] : data;

      if (!raw?.link) { setError('No se pudo generar el link de pago. Intenta de nuevo.'); return; }

      setEtapa('esperando');
      iniciarPollingYContador();

      // Abrir en Chrome externo
      window.open(raw.link, '_system');
    } catch {
      setError('Error de conexión. Intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const handleCerrar = () => {
    detener();
    onHide();
    window.location.reload();
  };

  const handleCancelar = () => {
    detener();
    setEtapa('inicio');
    onHide();
  };

  // ── Comprobante ──────────────────────────────────────────────────
  if (etapa === 'comprobante' && svgBase64) {
    return (
      <Modal show onHide={handleCerrar} centered>
        <Modal.Header closeButton style={{ borderBottom: 'none' }}>
          <Modal.Title style={{ fontSize: 15, fontWeight: 700 }}>🧾 Comprobante de Pago</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ComprobanteTicket svgBase64={svgBase64} facturaId={facturaId || ''} onClose={handleCerrar} />
        </Modal.Body>
      </Modal>
    );
  }

  // ── Pagado ───────────────────────────────────────────────────────
  if (etapa === 'pagado') {
    return (
      <Modal show onHide={handleCerrar} centered>
        <Modal.Body className="text-center py-4 px-3">
          <div style={{ fontSize: 72 }}>🎉</div>
          <h4 className="mt-2 fw-bold text-success">¡Pago exitoso!</h4>
          <p className="text-muted mt-2 mb-1">El pago de <strong>${monto.toFixed(2)}</strong> fue procesado correctamente.</p>
          <p className="text-muted mb-3">Factura <strong>#{facturaId}</strong> — <strong>{nombre}</strong></p>
          <div className="d-flex gap-2 mt-3">
            {svgBase64 && (
              <Button variant="success" className="w-100" onClick={() => setEtapa('comprobante')}>
                🧾 Ver comprobante
              </Button>
            )}
            <Button variant="outline-secondary" className="w-100" onClick={handleCerrar}>Cerrar</Button>
          </div>
        </Modal.Body>
      </Modal>
    );
  }

  // ── Timeout ──────────────────────────────────────────────────────
  if (etapa === 'timeout') {
    const verificarManual = async () => {
      resolvedRef.current = false;
      const pagado = await consultarEstado();
      if (!pagado) resolvedRef.current = true; // vuelve a modo timeout si no pagó
    };
    return (
      <Modal show onHide={handleCerrar} centered>
        <Modal.Body className="text-center py-4 px-3">
          <div style={{ fontSize: 56 }}>⏰</div>
          <h4 className="mt-2 fw-bold text-secondary">Tiempo agotado</h4>
          <p className="text-muted mt-2 mb-3">
            No se detectó el pago en 3 minutos.<br />
            Si ya pagaste, puedes verificarlo manualmente.
          </p>
          <Button variant="primary" className="w-100 mb-2" onClick={verificarManual}>
            🔄 Comprobar mi pago
          </Button>
          <Button variant="outline-secondary" className="w-100" onClick={handleCerrar}>Cerrar</Button>
        </Modal.Body>
      </Modal>
    );
  }

  // ── Esperando pago (polling activo) ─────────────────────────────
  if (etapa === 'esperando') {
    const pct = Math.round(((TIMEOUT_SEG - segundos) / TIMEOUT_SEG) * 100);
    return (
      <Modal show onHide={() => {}} centered backdrop="static">
        <Modal.Body className="text-center py-4 px-3">
          <Spinner animation="border" variant="primary" style={{ width: 52, height: 52 }} />
          <p className="mt-3 mb-1 fw-bold" style={{ fontSize: 16 }}>Verificando tu pago...</p>
          <p className="text-muted mb-1" style={{ fontSize: 13 }}>
            Factura <strong>#{facturaId}</strong> — ${monto.toFixed(2)}
          </p>
          <p className="text-muted mb-3" style={{ fontSize: 12 }}>
            Consulta automática cada {POLL_SEG}s · Tiempo restante: <strong>{fmtTime(segundos)}</strong>
          </p>
          {/* Barra de progreso */}
          <div style={{ background: 'var(--sn-superficie-3)', borderRadius: 8, height: 6, marginBottom: 16 }}>
            <div style={{ background: 'var(--sn-marca)', borderRadius: 8, height: 6, width: `${pct}%`, transition: 'width 1s linear' }} />
          </div>
          <Button
            variant="outline-primary"
            size="sm"
            className="mb-2 w-100"
            onClick={() => consultarEstado()}
          >
            🔄 Verificar ahora
          </Button>
          <Button
            variant="link"
            size="sm"
            className="text-muted w-100"
            style={{ fontSize: 12 }}
            onClick={handleCancelar}
          >
            Cancelar
          </Button>
        </Modal.Body>
      </Modal>
    );
  }

  // ── Modal principal ──────────────────────────────────────────────
  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton style={{ borderBottom: 'none', paddingBottom: 0 }}>
        <Modal.Title style={{ fontSize: 16, fontWeight: 700 }}>💳 Pago con Payphone</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ paddingTop: 12 }}>
        <div style={{ background: 'var(--sn-superficie-2)', borderRadius: 12, padding: '14px 16px', marginBottom: 12, border: '1px solid var(--sn-borde)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
            <div style={{ flex: 1, marginRight: 10 }}>
              <div style={{ fontSize: 10, color: 'var(--sn-texto-3)', textTransform: 'uppercase', letterSpacing: 0.5 }}>👤 Cliente</div>
              <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--sn-texto)', marginTop: 2 }}>{nombre}</div>
            </div>
            {estadoUp && (
              <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20, background: estadoBg, color: estadoColor, whiteSpace: 'nowrap' }}>
                {estadoEmoji} {estadoUp}
              </span>
            )}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
            <div>
              <div style={{ fontSize: 10, color: 'var(--sn-texto-3)', textTransform: 'uppercase', letterSpacing: 0.5 }}>🪪 Cédula</div>
              <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--sn-texto-2)', marginTop: 2 }}>{cedula}</div>
            </div>
            {facturaId && (
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 10, color: 'var(--sn-texto-3)', textTransform: 'uppercase', letterSpacing: 0.5 }}>🧾 Factura</div>
                <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--sn-texto-2)', marginTop: 2 }}>#{facturaId}</div>
              </div>
            )}
          </div>
          <div style={{ borderTop: '1px solid var(--sn-borde)', paddingTop: 10 }}>
            <div style={{ fontSize: 10, color: 'var(--sn-texto-3)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 2 }}>💰 Monto a pagar</div>
            <div style={{ fontWeight: 700, fontSize: 32, color: 'var(--sn-alerta-texto)', lineHeight: 1 }}>${monto.toFixed(2)}</div>
          </div>
        </div>
        {error && <p style={{ fontSize: 12, color: 'var(--sn-peligro)', marginBottom: 8 }}>{error}</p>}
        <p style={{ fontSize: 12, color: 'var(--sn-texto-3)', margin: 0 }}>🔒 Pago seguro procesado por Payphone</p>
      </Modal.Body>
      <Modal.Footer style={{ borderTop: 'none', paddingTop: 0 }}>
        <Button variant="outline-secondary" onClick={onHide} disabled={loading}>Cancelar</Button>
        <Button variant="primary" onClick={handlePagar} disabled={loading}>
          {loading ? <><Spinner animation="border" size="sm" className="me-2" />Generando...</> : '🌐 Abrir ventana de pago'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default PayphoneClienteModal;
